import React, { useState, useEffect } from 'react';
import { productService, categoryService } from '../services/api';
import ProductCard from '../components/ProductCard';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaShippingFast, FaShieldAlt, FaHeadset, FaTags } from 'react-icons/fa';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    category: '',
    priceRange: '',
    sortBy: 'default'
  });

  useEffect(() => {
    console.log('ProductsPage: Starting data fetch');
    const fetchData = async () => {
      try {
        console.log('ProductsPage: Fetching data from API');
        const [productsRes, categoriesRes] = await Promise.all([
          productService.getAllProducts(),
          categoryService.getAllCategories()
        ]);
        
        console.log('ProductsPage: Products data received:', productsRes.data);
        console.log('ProductsPage: Categories data received:', categoriesRes.data);
        
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
        console.log('ProductsPage: Data fetch complete');
      }
    };

    fetchData();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const resetFilters = () => {
    setFilters({
      category: '',
      priceRange: '',
      sortBy: 'default'
    });
  };

  const getFilteredProducts = () => {
    let filtered = [...products];
    
    // Filter by category
    if (filters.category) {
      filtered = filtered.filter(product => 
        product.category_id === parseInt(filters.category)
      );
    }
    
    // Filter by price range
    if (filters.priceRange) {
      const [min, max] = filters.priceRange.split('-').map(Number);
      filtered = filtered.filter(product => 
        product.price >= min && (max ? product.price <= max : true)
      );
    }
    
    // Sort products
    switch (filters.sortBy) {
      case 'price-low-high':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high-low':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name-a-z':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'name-z-a':
        filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
        break;
      default:
        // Default sort (featured or newest)
        break;
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  if (loading) {
    return (
      <Container className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <div style={{ background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', marginBottom: 32, padding: '2.5rem 0 2rem 0' }}>
        <Container>
          <h1 className="fw-bold text-center mb-2" style={{ fontSize: '2.2rem', color: '#222', letterSpacing: 1 }}>Discover Our Products</h1>
          <p className="text-center mb-4" style={{ margin: '0 auto', color: '#444', fontSize: 18 }}>
            Explore a curated selection of premium office furniture, tech, and essentials—perfect <br/>  for startups, businesses, and home offices. Quality, comfort, and style delivered to your workspace.
          </p>
        </Container>
      </div>
      <Container style={{ padding: '24px 0'}}>
        {/* Why Shop With Us */}
        <Row>
          {/* Filters Sidebar */}
          <Col lg={3} className="mb-4">
            <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, border: '2px solid #e0e7ff' }}>
              <h2 className="h5 mb-3">Filters</h2>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select 
                    name="category" 
                    value={filters.category}
                    onChange={handleFilterChange}
                    style={{ borderRadius: 10, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Price Range</Form.Label>
                  <Form.Select 
                    name="priceRange" 
                    value={filters.priceRange}
                    onChange={handleFilterChange}
                    style={{ borderRadius: 10, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                  >
                    <option value="">Any Price</option>
                    <option value="0-100">₹0 - ₹100</option>
                    <option value="100-200">₹100 - ₹200</option>
                    <option value="200-500">₹200 - ₹500</option>
                    <option value="500-1000">₹500 - ₹1000</option>
                    <option value="1000">₹1000+</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Sort By</Form.Label>
                  <Form.Select 
                    name="sortBy" 
                    value={filters.sortBy}
                    onChange={handleFilterChange}
                    style={{ borderRadius: 10, background: '#f8fafc', border: '1.5px solid #e0e7ef' }}
                  >
                    <option value="default">Featured</option>
                    <option value="price-low-high">Price: Low to High</option>
                    <option value="price-high-low">Price: High to Low</option>
                    <option value="name-a-z">Name: A to Z</option>
                    <option value="name-z-a">Name: Z to A</option>
                  </Form.Select>
                </Form.Group>
                <Button 
                  variant="outline-secondary" 
                  className="w-100"
                  onClick={resetFilters}
                  style={{ borderRadius: 10, fontWeight: 600 }}
                >
                  Reset Filters
                </Button>
              </Form>
            </div>
          </Col>
          {/* Products Grid */}
          <Col lg={9}>
            <Row>
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <Col key={product.id} lg={4} md={6} sm={6} className="mb-4">
                      <ProductCard product={product} />
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <Alert variant="info">
                    No products match your selected filters. Try adjusting your filters or browse all products.
                  </Alert>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Container>
      <Row style={{ background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', padding: '3rem' }} >
          <Col xs={12} md={6} lg={3} className="mb-3 d-flex justify-content-center">
            <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, textAlign: 'center', border: '2px solid #e0e7ff', width: '100%' }}>
              <FaShippingFast size={36} style={{ color: '#4f46e5', marginBottom: 10 }} />
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>Fast Delivery</div>
              <div style={{ color: '#666', fontSize: 14 }}>Quick, reliable shipping on every order</div>
            </div>
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3 d-flex justify-content-center">
            <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, width: '100%', textAlign: 'center', border: '2px solid #e0e7ff' }}>
              <FaShieldAlt size={36} style={{ color: '#4f46e5', marginBottom: 10 }} />
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>Quality Guarantee</div>
              <div style={{ color: '#666', fontSize: 14 }}>Premium products, built to last</div>
            </div>
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3 d-flex justify-content-center">
            <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, width: '100%', textAlign: 'center', border: '2px solid #e0e7ff' }}>
              <FaHeadset size={36} style={{ color: '#4f46e5', marginBottom: 10 }} />
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>Expert Support</div>
              <div style={{ color: '#666', fontSize: 14 }}>Friendly help, whenever you need it</div>
            </div>
          </Col>
          <Col xs={12} md={6} lg={3} className="mb-3 d-flex justify-content-center">
            <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, width: '100%', textAlign: 'center', border: '2px solid #e0e7ff' }}>
              <FaTags size={36} style={{ color: '#4f46e5', marginBottom: 10 }} />
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>Best Value</div>
              <div style={{ color: '#666', fontSize: 14 }}>Competitive prices, great deals</div>
            </div>
          </Col>
        </Row>
    </>
  );
};

export default ProductsPage; 